import { useState, useMemo } from "react";
import { trackEvent } from "@/lib/analytics";
import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./readiness.css";

interface Question {
  text: string;
  options: [string, string, string, string];
  gap: { description: string; api: string; endpoint: string; docsHref: string };
}
interface Category { name: string; icon: string; questions: Question[]; }

const CATEGORIES: Category[] = [
  {
    name: "EHR & Terminology", icon: "fa-book-medical",
    questions: [
      { text: "How does your facility look up medical codes (ICD-10, CPT, HCPCS) during clinical workflows?", options: ["No automated lookup — staff search manually or use paper references", "Basic lookup via EHR but limited to a single code system", "EHR provides lookup across multiple systems but data may be outdated", "Real-time, multi-system code search integrated into clinical workflows"], gap: { description: "Lack of automated, cross-system code lookup delays coding and introduces errors", api: "Terminology Search API", endpoint: "GET /v1/search?q={term}&systems=icd10,cpt", docsHref: "/docs/search-apis/overview" } },
      { text: "How current are the drug terminology datasets (NDC, RxNorm) your systems rely on?", options: ["Unknown — we don't track dataset freshness", "Updated quarterly or less frequently", "Updated monthly with some manual processes", "Automatically updated from authoritative sources at least weekly"], gap: { description: "Stale drug datasets cause formulary mismatches and e-prescribing errors", api: "NDC & RxNorm APIs", endpoint: "GET /v1/ndc/{code} | GET /v1/rxnorm/{rxcui}", docsHref: "/docs/value-apis/ndc" } },
      { text: "Can your systems verify provider credentials (NPI, taxonomy, address) in real time?", options: ["No — provider data is entered manually and rarely verified", "Periodic batch verification against NPPES downloads", "On-demand lookup available but not integrated into registration workflows", "Automated real-time NPI verification at registration and referral"], gap: { description: "Unverified provider data leads to claim rejections and referral failures", api: "NPI Lookup API", endpoint: "GET /v1/npi/{number}", docsHref: "/docs/value-apis/npi" } },
      { text: "How does your facility map codes between different terminology systems (e.g., ICD-10 to SNOMED)?", options: ["No cross-system mapping — codes stay in their original system", "Manual mapping tables maintained by coding staff", "Some automated mappings but limited to one or two system pairs", "Comprehensive cross-walk mappings available across all major systems"], gap: { description: "Missing cross-system mappings break interoperability with external partners", api: "Terminology Cross-walk API", endpoint: "GET /v1/icd10/{code} | GET /v1/loinc/{code}", docsHref: "/docs/value-apis/icd10" } },
      { text: "How does your facility manage vaccine code lookups and immunization reporting?", options: ["Manual tracking — no automated CVX/MVX code resolution", "Basic EHR integration but codes are not validated against current CVX set", "CVX lookups available but immunization registry reporting is manual", "Automated CVX validation with direct immunization registry submission"], gap: { description: "Outdated vaccine codes cause immunization registry rejections", api: "CVX Vaccine Code API", endpoint: "GET /v1/cvx/{code}", docsHref: "/docs/value-apis/cvx" } },
    ],
  },
  {
    name: "Claims & Revenue Cycle", icon: "fa-file-invoice-dollar",
    questions: [
      { text: "Does your facility validate claims against NCCI edits before submission?", options: ["No pre-submission validation — edits caught only by payer rejections", "Manual review of high-risk claims by coding staff", "Some automated checks but not comprehensive NCCI PTP coverage", "Full automated NCCI PTP edit validation on every claim before submission"], gap: { description: "Missing NCCI edit checks cause preventable claim denials", api: "NCCI PTP Edit API", endpoint: "GET /v1/ncci/ptp?code1={cpt1}&code2={cpt2}", docsHref: "/docs/value-apis/icd10" } },
      { text: "What is your facility's claim denial rate, and how are denials tracked?", options: ["Denial rate unknown — no systematic tracking", "Tracked manually; denial rate above 15%", "Tracked in billing system; denial rate 8–15% with some root-cause analysis", "Below 8% with automated root-cause analysis and prevention workflows"], gap: { description: "High denial rates without root-cause analysis drain rural revenue cycles", api: "Claims Validation API", endpoint: "POST /v1/claims/validate", docsHref: "/docs/value-apis/icd10" } },
      { text: "How does your facility check patient coverage and eligibility before services?", options: ["No real-time eligibility checks — coverage verified manually or after service", "Phone/portal checks for some payers but not automated", "Automated eligibility for major payers but gaps with smaller plans", "Real-time eligibility verification across all payers at point of service"], gap: { description: "Lack of eligibility verification leads to unbillable services", api: "Coverage Validation API", endpoint: "POST /v1/claims/validate", docsHref: "/docs/value-apis/icd10" } },
      { text: "Does your billing system enforce MUE (Medically Unlikely Edit) unit limits?", options: ["No MUE checking — unit limits not validated before submission", "Manual review for known high-risk codes only", "Automated MUE checks for some code families", "Comprehensive MUE validation across all CPT/HCPCS codes on every claim"], gap: { description: "MUE violations are a top reason for claim line-item denials", api: "MUE Limit Check API", endpoint: "GET /v1/ncci/mue?code={cpt}", docsHref: "/docs/value-apis/icd10" } },
      { text: "How does your facility handle code-pair edits and modifier requirements?", options: ["No awareness of code-pair restrictions — caught only at denial", "Coding staff review for common conflicts manually", "Partial automation for high-frequency code pairs", "Full NCCI PTP column-1/column-2 and modifier validation on every claim"], gap: { description: "Missed code-pair edits and modifier errors cause rework and lost revenue", api: "NCCI PTP Edit API", endpoint: "GET /v1/ncci/ptp?code1={cpt1}&code2={cpt2}", docsHref: "/docs/value-apis/icd10" } },
    ],
  },
  {
    name: "Connectivity & Interoperability", icon: "fa-network-wired",
    questions: [
      { text: "Can your facility exchange clinical data (C-CDA, FHIR) with external providers?", options: ["No electronic clinical data exchange — fax and mail only", "Direct Secure Messaging set up with a few partners", "C-CDA exchange via HIE for some use cases; FHIR not yet adopted", "FHIR-based exchange with multiple partners and automated data reconciliation"], gap: { description: "Inability to exchange clinical data electronically isolates rural facilities", api: "FHIR Endpoint Directory", endpoint: "GET /v1/endpoints?org={name}&type=fhir", docsHref: "/docs/search-apis/overview" } },
      { text: "How FHIR-ready is your EHR system?", options: ["Our EHR does not support FHIR APIs", "FHIR R4 APIs available but not enabled or tested", "FHIR APIs enabled for patient access; limited bulk/backend use", "Full FHIR R4 support with backend services, bulk data, and SMART-on-FHIR"], gap: { description: "Lack of FHIR readiness blocks compliance with CMS interoperability rules", api: "FHIR Endpoint Directory", endpoint: "GET /v1/endpoints?org={name}&type=fhir", docsHref: "/docs/search-apis/overview" } },
      { text: "Can your facility discover payer endpoints for electronic transactions?", options: ["No — payer connectivity is configured manually per payer", "Clearinghouse handles routing but we have no visibility into endpoints", "We can look up some payer endpoints but the process is manual", "Automated payer endpoint discovery and monitoring across all transaction types"], gap: { description: "Manual payer endpoint management wastes staff time and causes transaction failures", api: "Payer Endpoint Discovery API", endpoint: "GET /v1/endpoints?payer={name}", docsHref: "/docs/search-apis/overview" } },
      { text: "Is your facility connected to a Health Information Exchange (HIE)?", options: ["Not connected to any HIE", "Connected to state HIE but only for ADT notifications", "Active HIE participant with query-based exchange for some data types", "Full HIE participation with bi-directional data flow and TEFCA readiness"], gap: { description: "No HIE connectivity limits care coordination and duplicates testing", api: "Connectivity Intelligence API", endpoint: "GET /v1/endpoints?state={st}&type=hie", docsHref: "/docs/search-apis/overview" } },
      { text: "How does your facility monitor the health and availability of integration endpoints?", options: ["No monitoring — failures discovered when transactions fail", "Manual spot-checks on critical connections", "Automated monitoring for some endpoints with email alerts", "Comprehensive endpoint monitoring with dashboards, alerting, and failover"], gap: { description: "Unmonitored endpoints cause silent failures in clinical and billing workflows", api: "Endpoint Monitoring API", endpoint: "GET /v1/endpoints?status=active", docsHref: "/docs/search-apis/overview" } },
    ],
  },
];

const TOTAL_QUESTIONS = CATEGORIES.reduce((sum, c) => sum + c.questions.length, 0);
const MAX_SCORE = TOTAL_QUESTIONS * 3;
type Step = 1 | 2 | 3 | 4;
type Tier = { label: string; fg: string; bar: string };

function getTier(pct: number): Tier {
  if (pct <= 25) return { label: "Beginning", fg: "#b6423a", bar: "#b6423a" };
  if (pct <= 50) return { label: "Developing", fg: "#a9791a", bar: "#d6a224" };
  if (pct <= 75) return { label: "Progressing", fg: "#1f6fb2", bar: "#1f6fb2" };
  return { label: "Advanced", fg: "#3f8a58", bar: "#3f8a58" };
}

export default function ReadinessCheckerPage() {
  const [step, setStep] = useState<Step>(1);
  const [answers, setAnswers] = useState<Record<string, number>>({});

  const currentCategory = step <= 3 ? CATEGORIES[step - 1]! : null;
  const currentStepComplete = useMemo(() => {
    if (step > 3) return true;
    return CATEGORIES[step - 1]!.questions.every((_, qi) => answers[`${step - 1}-${qi}`] !== undefined);
  }, [step, answers]);

  const categoryScores = useMemo(() => CATEGORIES.map((cat, ci) => {
    const sum = cat.questions.reduce((acc, _, qi) => acc + (answers[`${ci}-${qi}`] ?? 0), 0);
    const max = cat.questions.length * 3;
    const pct = Math.round((sum / max) * 100);
    return { sum, max, pct, tier: getTier(pct) };
  }), [answers]);

  const overallScore = useMemo(() => {
    const sum = Object.values(answers).reduce((acc, v) => acc + v, 0);
    const pct = Math.round((sum / MAX_SCORE) * 100);
    return { sum, pct, tier: getTier(pct) };
  }, [answers]);

  const gaps = useMemo(() => {
    const result: { categoryName: string; questionText: string; score: number; gap: Question["gap"] }[] = [];
    CATEGORIES.forEach((cat, ci) => cat.questions.forEach((q, qi) => {
      const score = answers[`${ci}-${qi}`];
      if (score !== undefined && score <= 1) result.push({ categoryName: cat.name, questionText: q.text, score, gap: q.gap });
    }));
    return result;
  }, [answers]);

  function handleAnswer(questionIdx: number, value: number) {
    if (step > 3) return;
    setAnswers((prev) => ({ ...prev, [`${step - 1}-${questionIdx}`]: value }));
  }
  function goNext() {
    if (step === 3) trackEvent({ action: "readiness_completed", category: "tools", label: `score_${overallScore.pct}` });
    if (step < 4) setStep((step + 1) as Step);
  }
  function goBack() { if (step > 1) setStep((step - 1) as Step); }
  function restart() { setAnswers({}); setStep(1); }

  const optLabels = ["Not started", "Basic", "Progressing", "Mature"];

  return (
    <div className="ffly ffly--public readiness-page">
      <PageMeta
        title="Rural Health Readiness Checker"
        description="Free interoperability readiness assessment for rural hospitals. Identify gaps across claims, terminology, and connectivity, and get a personalized FHIRfly API roadmap."
        canonical={canonicalUrl("/rural-health/readiness")}
      />
      <div className="print-hide"><FieldHeader /></div>

      <div className="hidden print-only" style={{ padding: "0 28px", marginTop: 20 }}>
        <h1 style={{ fontFamily: "var(--ffly-font-display)" }}>Rural Health Readiness Report — FHIRfly</h1>
        <p style={{ color: "var(--ffly-muted)" }}>Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
      </div>

      <div className="wrap">
        <section className="hero print-hide">
          <span className="eyebrow"><i className="fa-solid fa-clipboard-check" />Free assessment</span>
          <h1>Rural Health Readiness Checker</h1>
          <p className="lede">Assess your facility's interoperability maturity across three key areas, identify gaps, and get a personalized roadmap to the FHIRfly APIs that fill them.</p>
        </section>

        {/* progress */}
        <div className="prog print-hide">
          {[{ n: 1, l: "EHR & Terminology" }, { n: 2, l: "Claims & Revenue" }, { n: 3, l: "Connectivity" }, { n: 4, l: "Results" }].map((s, i, arr) => (
            <div key={s.n} style={{ display: "contents" }}>
              <div className={`pstep${step >= s.n ? " on" : ""}`}>
                <div className="pcircle">{step > s.n ? <i className="fa-solid fa-check" style={{ fontSize: 12 }} /> : s.n}</div>
                <span className="plabel">{s.l}</span>
              </div>
              {i < arr.length - 1 && <div className={`pconn${step > s.n ? " on" : ""}`} />}
            </div>
          ))}
        </div>

        {/* steps 1-3 */}
        {step <= 3 && currentCategory && (
          <div>
            <div className="stephead">
              <div className="ic"><i className={`fa-solid ${currentCategory.icon}`} /></div>
              <div><h2>{currentCategory.name}</h2><p>Answer all 5 questions to continue</p></div>
            </div>
            {currentCategory.questions.map((q, qi) => {
              const sel = answers[`${step - 1}-${qi}`];
              return (
                <div className="qcard" key={qi}>
                  <p className="qt"><span className="qn">Q{qi + 1}.</span>{q.text}</p>
                  <div className="opts">
                    {q.options.map((opt, oi) => (
                      <button key={oi} className={`opt${sel === oi ? " sel" : ""}`} onClick={() => handleAnswer(qi, oi)}>
                        <span className="radio" />
                        <span><span className="lvl">{optLabels[oi]}</span><span className="dash">—</span>{opt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* step 4: results */}
        {step === 4 && (
          <div>
            <div className="overall">
              <div className="k">Overall readiness score</div>
              <div className="pct">{overallScore.pct}%</div>
              <div><span className="tier-badge" style={{ color: overallScore.tier.fg }}>{overallScore.tier.label}</span></div>
              <div className="barwrap"><div className="scorebar"><span style={{ width: `${overallScore.pct}%`, background: overallScore.tier.bar }} /></div></div>
            </div>

            <div className="catscores">
              {CATEGORIES.map((cat, ci) => {
                const sc = categoryScores[ci]!;
                return (
                  <div className="catscore print-no-break" key={cat.name}>
                    <div className="top"><div className="ic"><i className={`fa-solid ${cat.icon}`} /></div><span className="nm">{cat.name}</span></div>
                    <div className="v">{sc.pct}%</div>
                    <div className="scorebar"><span style={{ width: `${sc.pct}%`, background: sc.tier.bar }} /></div>
                    <span className="tl" style={{ color: sc.tier.fg }}>{sc.tier.label}</span>
                  </div>
                );
              })}
            </div>

            {gaps.length > 0 ? (
              <div className="gaps">
                <h2><i className="fa-solid fa-triangle-exclamation" style={{ color: "var(--ffly-sun)", marginRight: 8 }} />Identified gaps ({gaps.length})</h2>
                {gaps.map((g, i) => (
                  <div className="gapcard print-no-break" key={i}>
                    <div className="cat">{g.categoryName}</div>
                    <div className="desc">{g.gap.description}</div>
                    <div className="q">{g.questionText}</div>
                    <div className="rec">
                      <div className="api"><i className="fa-solid fa-plug" />Recommended: {g.gap.api}</div>
                      <code>{g.gap.endpoint}</code>
                      <Link to={g.gap.docsHref}>View documentation →</Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="nogaps">
                <i className="fa-solid fa-circle-check" />
                <div className="t">No critical gaps identified</div>
                <p>Your facility scored above baseline across all areas. FHIRfly APIs can still help you reach Advanced maturity.</p>
              </div>
            )}

            <div className="rctas print-hide" style={{ marginTop: 28 }}>
              <Link className="leaf" to="/dashboard">Get a free API key →</Link>
              <a className="ghost" href="mailto:partnerships@fhirfly.io?subject=Rural Health Partnership">Contact partnerships</a>
              <button className="ghost" onClick={() => window.print()}>Print report</button>
            </div>
          </div>
        )}

        {/* nav */}
        <div className="nav print-hide">
          <button className="ghost" onClick={goBack} disabled={step === 1}>← Back</button>
          {step < 3 && <button className="leaf" onClick={goNext} disabled={!currentStepComplete}>Next →</button>}
          {step === 3 && <button className="leaf" onClick={goNext} disabled={!currentStepComplete}>View results →</button>}
          {step === 4 && <button className="ghost" onClick={restart}>↻ Start over</button>}
        </div>
      </div>

      <div className="print-hide"><FieldFooter variant="full" /></div>
    </div>
  );
}
